import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Mail, Lock, ArrowLeft, Loader2, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface SignInModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type ModalMode = 'signin' | 'signup' | 'recovery' | 'reset';

export default function SignInModal({ isOpen, onClose, onSuccess }: SignInModalProps) {
  const [mode, setMode] = useState<ModalMode>('signin');
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [error, setError] = useState("");
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  
  const { signIn, signUp, requestPasswordReset, resetPassword } = useAuth();
  
  const resetForm = () => {
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setTermsAccepted(false);
    setError("");
    setIsEmailSent(false);
    setSignUpSuccess(false);
    setMode('signin');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    
    try {
      const { error } = await signIn(email, password);
      if (error) {
        setError(error.message);
      } else {
        handleClose();
        onSuccess?.();
      }
    } catch (err: any) {
      setError(err.message || "An error occurred during sign in");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (!termsAccepted) {
      setError("You must agree to the Terms & Conditions to create an account");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { error, needsConfirmation, success } = await signUp(email, password);
      if (error) {
        setError(error.message);
      } else if (success) {
        // Update profile with terms acceptance
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('profiles')
            .update({ 
              terms_accepted: true, 
              terms_accepted_at: new Date().toISOString() 
            })
            .eq('id', user.id);
        }

        setSignUpSuccess(true);
        if (!needsConfirmation) {
          // If no email confirmation needed, close modal and trigger success
          setTimeout(() => {
            handleClose();
            onSuccess?.();
          }, 2000);
        }
      }
    } catch (err: any) {
      setError(err.message || "An error occurred during sign up");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    
    try {
      const { error, success } = await requestPasswordReset(email);
      if (error) {
        setError(error.message);
      } else if (success) {
        setIsEmailSent(true);
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { error, success } = await resetPassword(password);
      if (error) {
        setError(error.message);
      } else if (success) {
        handleClose();
        onSuccess?.();
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const renderSignInForm = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold font-display">Welcome back</h2>
        <p className="text-muted-foreground mt-2">Sign in to your account</p>
      </div>
      
      {error && (
        <div className="p-3 bg-destructive/10 border border-destructive/30 text-destructive rounded-md text-sm">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSignIn} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="flex gap-1 items-center">
            <Mail className="h-4 w-4" /> Email
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="h-11"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="password" className="flex gap-1 items-center">
            <Lock className="h-4 w-4" /> Password
          </Label>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="h-11"
          />
        </div>
        
        <Button
          type="submit"
          className="w-full h-11 text-base"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Signing in...
            </>
          ) : (
            "Sign In"
          )}
        </Button>
      </form>
      
      <div className="text-center space-y-2">
        <button
          type="button"
          onClick={() => setMode('recovery')}
          className="text-primary underline underline-offset-4 text-sm hover:no-underline block w-full"
        >
          Forgot your password?
        </button>
        <button
          type="button"
          onClick={() => setMode('signup')}
          className="text-muted-foreground text-sm hover:text-primary"
        >
          Don't have an account? <span className="text-primary underline underline-offset-4 hover:no-underline">Sign Up</span>
        </button>
      </div>
    </div>
  );

  const renderSignUpForm = () => {
    if (signUpSuccess) {
      return (
        <div className="text-center space-y-4">
          <div className="p-4 bg-primary/10 border border-primary/30 text-primary rounded-md">
            <p className="font-medium">Account created successfully!</p>
          </div>
          <p className="text-muted-foreground text-sm">
            Please check your email at <strong>{email}</strong> to confirm your account before signing in.
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => setMode('signin')}
            className="w-full"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Continue to Sign In
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold font-display">Create Account</h2>
          <p className="text-muted-foreground mt-2">Join CraftyJoy today</p>
        </div>
        
        {error && (
          <div className="p-3 bg-destructive/10 border border-destructive/30 text-destructive rounded-md text-sm">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSignUp} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="flex gap-1 items-center">
              <Mail className="h-4 w-4" /> Email
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-11"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password" className="flex gap-1 items-center">
              <Lock className="h-4 w-4" /> Password
            </Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="h-11"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="flex gap-1 items-center">
              <Lock className="h-4 w-4" /> Confirm Password
            </Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="h-11"
            />
          </div>
          
          <div className="flex items-start space-x-2">
            <Checkbox
              id="terms-modal"
              checked={termsAccepted}
              onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
              required
            />
            <div className="text-sm leading-5">
              <label htmlFor="terms-modal" className="text-foreground cursor-pointer">
                I agree to the{" "}
                <Link 
                  to="/terms" 
                  className="text-primary underline underline-offset-4 hover:text-primary/80"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms & Conditions
                </Link>
              </label>
            </div>
          </div>
          
          <Button
            type="submit"
            className="w-full h-11 text-base"
            disabled={isLoading || !termsAccepted}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating account...
              </>
            ) : (
              <>
                <User className="mr-2 h-4 w-4" />
                Create Account
              </>
            )}
          </Button>
        </form>
        
        <div className="text-center">
          <button
            type="button"
            onClick={() => setMode('signin')}
            className="text-muted-foreground text-sm hover:text-primary"
          >
            Already have an account? <span className="text-primary underline underline-offset-4 hover:no-underline">Sign In</span>
          </button>
        </div>
      </div>
    );
  };

  const renderRecoveryForm = () => {
    if (isEmailSent) {
      return (
        <div className="text-center space-y-4">
          <div className="p-4 bg-primary/10 border border-primary/30 text-primary rounded-md">
            <p>Password reset instructions sent!</p>
          </div>
          <p className="text-muted-foreground text-sm">
            We've sent a password reset link to <strong>{email}</strong>. 
            Please check your email and click the link to reset your password.
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => setMode('signin')}
            className="w-full"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Sign In
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold font-display">Reset Password</h2>
          <p className="text-muted-foreground mt-2">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>
        
        {error && (
          <div className="p-3 bg-destructive/10 border border-destructive/30 text-destructive rounded-md text-sm">
            {error}
          </div>
        )}
        
        <form onSubmit={handlePasswordRecovery} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="flex gap-1 items-center">
              <Mail className="h-4 w-4" /> Email
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-11"
            />
          </div>
          
          <Button
            type="submit"
            className="w-full h-11 text-base"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              "Send Reset Link"
            )}
          </Button>
        </form>
        
        <div className="text-center">
          <button
            type="button"
            onClick={() => setMode('signin')}
            className="text-primary underline underline-offset-4 text-sm hover:no-underline"
          >
            <ArrowLeft className="h-4 w-4 inline mr-1" />
            Back to Sign In
          </button>
        </div>
      </div>
    );
  };

  const renderResetForm = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold font-display">Set New Password</h2>
        <p className="text-muted-foreground mt-2">
          Enter your new password below.
        </p>
      </div>
      
      {error && (
        <div className="p-3 bg-destructive/10 border border-destructive/30 text-destructive rounded-md text-sm">
          {error}
        </div>
      )}
      
      <form onSubmit={handlePasswordReset} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="password" className="flex gap-1 items-center">
            <Lock className="h-4 w-4" /> New Password
          </Label>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="h-11"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="flex gap-1 items-center">
            <Lock className="h-4 w-4" /> Confirm New Password
          </Label>
          <Input
            id="confirmPassword"
            type="password"
            placeholder="••••••••"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className="h-11"
          />
        </div>
        
        <Button
          type="submit"
          className="w-full h-11 text-base"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Updating...
            </>
          ) : (
            "Update Password"
          )}
        </Button>
      </form>
    </div>
  );

  const renderContent = () => {
    switch (mode) {
      case 'signin':
        return renderSignInForm();
      case 'signup':
        return renderSignUpForm();
      case 'recovery':
        return renderRecoveryForm();
      case 'reset':
        return renderResetForm();
      default:
        return renderSignInForm();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="sr-only">Authentication</DialogTitle>
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
